/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import {
    array, bool, func, object, string,
} from 'prop-types';
import { useSelector } from 'react-redux';

import MobileUIContainer from './MobileUIContainer';
import DesktopUIContainerV2 from './DesktopUIContainerV2';
import MobileCarousel from './MobileCarousel';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getEnterpriseId } from '../../../../../../state/ducks/Member/ducks/EnterpriseId/EnterpriseId-Selectors';
import { getIsBot } from '../../../../../../state/ducks/App/App-Selectors';
import { getIsAuthenticatedStatus, getIsPassportUserLoggedIn } from '../../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getPassportSubscriptionStatus } from '../../../../../../state/ducks/Common/Common-Selectors';
import validatePassport from '../../../../../helpers/validatePassport';
import { getPassportData } from '../../../../../../state/ducks/Passport/Passport-Selectors';
import DynamicPrice from '../../../GraphqlProductPage/Partials/GraphqlPDPSkus/DynamicPrice';
import Heading from '../../BuyItAgainProducts/BuyItAgainRecsGridUI/Heading';
import noop from '../../../../../helpers/noop';

const useStyles = makeStyles((theme) => ({
    main: (styles) => ({
        width: '100%',
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '32px 0px',
        [theme.breakpoints.down(600)]: {
            padding: '16px 0px 16px',
            marginBottom: '32px',
            marginTop: '32px',
        },
    }),
    root: {
        maxWidth: '1440px',
        margin: '0 auto',
        display: 'flex',
        gap: '16px',
        [theme.breakpoints.down(600)]: {
            padding: '0 16px',
        },
    },
    newDesign: {
        paddingRight: '0',
    },
    recentlyViewedSectionBGColor: (styles) => ({
        backgroundColor: styles?.backgroundColor,
    }),
    becauseYouViewedSectionBGColor: (styles) => ({
        backgroundColor: styles?.becauseYouViewedSectionBGColor,
    }),
}));

export default function RecentlyViewedGridUIV2({
    products, cmsData, isNewDesign, personalizedCarousel, handleClickCallback, dataTestId,
}) {
    const isMobile = useMediaQuery('(max-width: 600px)');

    const {
        minimum_rows_to_show,
        min_products_to_show,
        title_font_family: fontFamily,
        title_font_size: fontSize,
        title_font_style: fontStyle,
        title_font_color,
        background_color,
        primary_grid_background_color,
        secondary_grid_background_color,
        main_title: title,
    } = cmsData || {};

    const recentlyViewedSectionBGColor = background_color?.color || primary_grid_background_color?.color || '#FFF8F0';
    const becauseYouViewedSectionBGColor = secondary_grid_background_color?.color || '#FFF8F0';
    const classes = useStyles({ backgroundColor: recentlyViewedSectionBGColor, becauseYouViewedSectionBGColor });

    // Recently viewed title and subtitle
    const recentHeading = personalizedCarousel?.heading[0] || {};
    const recentSubHeading = personalizedCarousel?.heading[1] || {};

    const mainStyles = {
        fontFamily: recentHeading?.font_family,
        fontSize: recentHeading?.font_size,
        fontStyle: recentHeading?.font_style,
        color: recentHeading?.color?.color,
        textAlign: recentHeading?.align_title,
        fontFamily1: recentSubHeading?.font_family,
        fontSize1: recentSubHeading?.font_size,
        fontStyle1: recentSubHeading?.font_style,
        color1: recentSubHeading?.color?.color,
        backgroundColor: recentlyViewedSectionBGColor,
        textAlign1: recentSubHeading?.align_title,
    };

    const enterpriseId = useSelector((state) => getEnterpriseId(state));
    const [isDynamicPricesFetched, setIsDynamicPricesFetched] = useState(false);
    const [dynamicPriceIsLoading, setDynamicPriceLoading] = useState(true);
    const [productSkuPrices, setProductSkuPrices] = useState([]);
    const isBot = useSelector((state) => getIsBot(state));
    const isOptimizeScriptEnabled = useSelector((state) => getFeatureFlag('is-optimize-script-enabled')(state));
    const isDynamicPriceEnabled = useSelector((state) => getFeatureFlag('is-dynamic-pricing-enabled')(state));

    // Set customer type ie. Passport, Registered.
    const isPassportUser = useSelector(getIsPassportUserLoggedIn);
    const isAuthenticated = useSelector(getIsAuthenticatedStatus);
    const passportData = useSelector(getPassportData);
    const isPassportMember = validatePassport('isActivePassportUser', passportData);
    const isPassportAdded = useSelector(getPassportSubscriptionStatus);
    let customerTypeValue = [];
    if ((isPassportMember && isPassportUser) || isPassportAdded) {
        customerTypeValue = [{
            name: 'CUSTOMER_TYPE',
            value: 'Passport',
        }];
    } else if (isAuthenticated) {
        customerTypeValue = [{
            name: 'CUSTOMER_TYPE',
            value: 'Registered',
        }];
    }

    const dynamicPricingProducts = [...products].map((item) => ({ ...item, id: item.partNumber }));

    // Declaring Dynamic price call instance
    const dynamicPriceInstance = new DynamicPrice(dynamicPricingProducts, enterpriseId, false, '', false, customerTypeValue);
    const centerAligned = personalizedCarousel?.product_alignment === 'center';

    useEffect(() => {
        if (isBot || !isDynamicPriceEnabled) {
            setProductSkuPrices([]);
            setDynamicPriceLoading(false);
            return;
        }
        if ((isOptimizeScriptEnabled && isDynamicPriceEnabled) || isDynamicPricesFetched) {
            return;
        }

        const useZipcode = false;

        setDynamicPriceLoading(true);
        dynamicPriceInstance.call(useZipcode, '').then((productPrices) => {
            setProductSkuPrices(productPrices?.newPrices || []);
            setDynamicPriceLoading(false);
            setIsDynamicPricesFetched(true);
        }).catch(() => {
            setDynamicPriceLoading(false);
        });
    }, [
        isDynamicPriceEnabled,
        isOptimizeScriptEnabled,
        isDynamicPricesFetched,
    ]);

    if (isMobile) {
        return (
            <div className={`${classes.main} ${classes.recentlyViewedSectionBGColor}`}>
                <div className={classes.root}>
                    <Heading
                        title={recentHeading?.copy || 'Recently Viewed Products'}
                        subTitle={recentSubHeading?.copy || 'These gifts miss you. Take another look!'}
                        styles={mainStyles}
                    />
                </div>
                <div className={`${classes.root} ${isNewDesign ? classes.newDesign : ''}`}>
                    {isNewDesign
                        ? (
                            <MobileCarousel
                                products={products}
                                handleLinkOnClick={handleClickCallback}
                                dynamicPriceInstance={dynamicPriceInstance}
                                productSkuPrices={productSkuPrices}
                                dynamicPriceIsLoading={dynamicPriceIsLoading}
                                dataTestId={dataTestId}
                            />
                        )
                        : (
                            <MobileUIContainer
                                products={products}
                                minRowsToShow={minimum_rows_to_show || 1}
                                trackClickThrough={handleClickCallback}
                                containerBGColor={recentlyViewedSectionBGColor}
                                dynamicPriceInstance={dynamicPriceInstance}
                                productSkuPrices={productSkuPrices}
                                dynamicPriceIsLoading={dynamicPriceIsLoading}
                                centerAligned={centerAligned}
                                dataTestId={dataTestId}
                            />
                        )}
                </div>
            </div>
        );
    }

    return (
        <div className={`${classes.main}`}>
            <div className={classes.root}>
                <Heading
                    title={recentHeading?.copy || 'Recently Viewed Products'}
                    subTitle={recentSubHeading?.copy || 'These gifts miss you. Take another look!'}
                    styles={mainStyles}
                />
            </div>
            <div className={classes.root}>
                <DesktopUIContainerV2
                    products={products}
                    minRowsToShow={minimum_rows_to_show || 1}
                    handleClickCallback={handleClickCallback}
                    recentlyViewedSectionBGColor={recentlyViewedSectionBGColor}
                    becauseYouViewedSectionBGColor={becauseYouViewedSectionBGColor}
                    dynamicPriceInstance={dynamicPriceInstance}
                    productSkuPrices={productSkuPrices}
                    dynamicPriceIsLoading={dynamicPriceIsLoading}
                    personalizedCarousel={personalizedCarousel}
                    dataTestId={dataTestId}
                />
            </div>
        </div>
    );
}

RecentlyViewedGridUIV2.propTypes = {
    products: array,
    cmsData: object,
    isNewDesign: bool,
    personalizedCarousel: object,
    handleClickCallback: func,
    dataTestId: string,
};

RecentlyViewedGridUIV2.defaultProps = {
    products: [],
    cmsData: {},
    isNewDesign: false,
    personalizedCarousel: {},
    handleClickCallback: noop,
    dataTestId: '',
};
